import {
  getPreferredMessageByType,
  handleDynamicMessage,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { PreparedResponseType } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesTypes';
import { type ResolvedPreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesUtils';
import { type SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import {
  trackLocationRequestFailed,
  trackLocationRequestGeneratedFromPhrases,
  type LocationRequestPathway,
} from '../../gainsightTrackings/locationRequestTrackings';

export interface LocationRequestTrackingContext {
  pathwayType: LocationRequestPathway;
  callerID: string | null | undefined;
  conversationId?: string;
  isOutbound?: boolean;
}

/**
 * Resolves the authority's configured location-request message — minting a
 * token through the location-share API in the process — and reports the outcome
 * to Amplitude. Returns the resolved message, or null when it could not be
 * resolved, in which case the caller only has to surface the error to the user.
 *
 * Shared by the in-call chats and the outbound SMS composer so a click reports
 * identically from either, and so every way of failing gets a `failure_reason`
 * rather than showing up as an unexplained gap between clicked and generated.
 */
export const resolveTrackedLocationRequestMessage = async (
  preconfiguredMessages: PreconfiguredResponse[],
  locationData: SmartPhraseContext,
  context: LocationRequestTrackingContext,
): Promise<ResolvedPreconfiguredResponse | null> => {
  let locationMessage: PreconfiguredResponse;
  try {
    // Throws when the expected rapidsos system default is missing or the
    // messages never loaded.
    locationMessage = getPreferredMessageByType(
      preconfiguredMessages,
      PreparedResponseType.locationRequest,
    );
  } catch {
    trackLocationRequestFailed({
      ...context,
      failureReason: 'message_unavailable',
    });
    return null;
  }

  let resolvedMessage: ResolvedPreconfiguredResponse;
  try {
    resolvedMessage = await handleDynamicMessage(locationMessage, locationData);
  } catch {
    trackLocationRequestFailed({
      ...context,
      failureReason: 'phrase_resolution_failed',
    });
    return null;
  }

  // The configured message is authority-editable, so the location link phrase
  // isn't guaranteed to be present. The message still sends; it just carries no
  // link, which is a distinct outcome from the mint failing.
  const wasGenerated = trackLocationRequestGeneratedFromPhrases(
    resolvedMessage.resolvedPhrases,
    context,
  );
  if (!wasGenerated) {
    trackLocationRequestFailed({
      ...context,
      failureReason: 'link_phrase_absent',
    });
  }

  return resolvedMessage;
};
